import React, { useCallback, useEffect, useState, memo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, CheckCircle2, Crown, Building2, Users, Star, Receipt, Info, FileText, HandCoins, Globe, GraduationCap, Landmark, Sparkles } from 'lucide-react';
import { SlideAtmosphere } from '../components/deck/SlideAtmosphere';
import { PunjabiPlacard } from '../components/deck/PunjabiPlacard';
const FlippableCard = ({
  card
}: {
  card: any;
}) => {
  const [isFlipped, setIsFlipped] = useState(false);
  return <div className="aspect-[3/4] relative cursor-pointer group" style={{
    perspective: '1000px'
  }} onClick={() => setIsFlipped(!isFlipped)} data-id="element-7042">
      <motion.div className="w-full h-full relative" animate={{
      rotateY: isFlipped ? 180 : 0
    }} transition={{
      duration: 0.8,
      type: 'spring',
      stiffness: 200,
      damping: 25
    }} style={{
      transformStyle: 'preserve-3d'
    }} data-id="element-7043">
        {/* Front */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/20 to-black border border-[#D4AF37]/40 flex flex-col items-center justify-center overflow-hidden shadow-[0_0_30px_rgba(212,175,55,0.15)]" style={{
        backfaceVisibility: 'hidden'
      }} data-id="element-7044">
          <img src={card.src} alt={card.name} className="w-full h-full object-cover opacity-70 group-hover:opacity-100 transition-opacity duration-700" data-id="element-7045" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-80" data-id="element-7046" />
          <div className="absolute bottom-0 left-0 right-0 p-3 text-center" data-id="element-7047">
            <p className="text-[#D4AF37] text-xs font-light tracking-[0.2em] uppercase" data-id="element-7048">
              {card.name}
            </p>
          </div>
        </div>

        {/* Back */}
        <div className="absolute inset-0 bg-[#0a0604] border border-[#D4AF37]/40 flex flex-col items-center justify-center p-5 text-center shadow-[inset_0_0_40px_rgba(0,0,0,0.8)]" style={{
        transform: 'rotateY(180deg)',
        backfaceVisibility: 'hidden'
      }} data-id="element-7049">
          <div className="absolute inset-0 opacity-10 bg-[url('https://cdn.magicpatterns.com/uploads/3hBK2oMvmeZZy5P54xmeKQ/Lahore_Court_Collection.png')] bg-cover bg-center mix-blend-overlay" data-id="element-7050" />
          <div className="relative z-10 w-full" data-id="element-7051">
            <Crown className="w-6 h-6 text-[#D4AF37] mx-auto mb-4 opacity-80" data-id="element-7052" />
            <h4 className="text-[#FDFBF7] font-serif text-lg mb-3" data-id="element-7053">
              {card.name}
            </h4>
            <div className="w-12 h-px bg-[#D4AF37]/50 mx-auto mb-4" data-id="element-7054" />
            <p className="text-white/70 text-xs font-light leading-relaxed mb-6" data-id="element-7055">
              {card.desc}
            </p>
            {card.stats && <div className="grid grid-cols-2 gap-3 text-left bg-black/40 p-3 border border-[#D4AF37]/20 backdrop-blur-sm" data-id="element-7056">
                {card.stats.map((stat: any, idx: number) => <div key={idx} data-id="element-7057">
                    <p className="text-[#D4AF37] text-[9px] uppercase tracking-widest mb-0.5" data-id="element-7058">
                      {stat.label}
                    </p>
                    <p className="text-white/90 text-xs font-light" data-id="element-7059">
                      {stat.value}
                    </p>
                  </div>)}
              </div>}
          </div>
        </div>
      </motion.div>
    </div>;
};
export const SponsorDeckPage = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const totalSlides = 22;
  const nextSlide = useCallback(() => {
    setActiveSlide(prev => Math.min(prev + 1, totalSlides - 1));
  }, []);
  const prevSlide = useCallback(() => {
    setActiveSlide(prev => Math.max(prev - 1, 0));
  }, []);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Space') nextSlide();else if (e.key === 'ArrowLeft') prevSlide();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide]);
  return <div className="bg-black w-screen h-screen overflow-hidden text-white relative font-sans" data-id="element-7060">
      {/* Fixed Chrome */}
      <motion.div className="fixed top-0 left-0 right-0 h-1 bg-[#D4AF37] origin-left z-50" initial={{
      scaleX: 0
    }} animate={{
      scaleX: activeSlide / (totalSlides - 1)
    }} transition={{
      duration: 0.5,
      ease: 'easeInOut'
    }} data-id="element-7061" />

      <Link to="/" className="fixed top-8 left-8 z-40 flex items-center gap-3 text-white/50 hover:text-[#D4AF37] transition-colors text-xs tracking-widest uppercase font-light" data-id="element-7062">
        <ArrowLeft className="w-4 h-4" data-id="element-7063" />
        Home
      </Link>

      <div className="fixed top-8 right-8 z-40 text-[#D4AF37]/60 text-xs font-light tracking-[0.3em]" data-id="element-7064">
        {String(activeSlide + 1).padStart(2, '0')} / {totalSlides}
      </div>

      <div className="fixed bottom-8 right-8 z-40 flex items-center gap-4" data-id="element-7065">
        <button onClick={prevSlide} disabled={activeSlide === 0} className="w-12 h-12 rounded-full border border-[#D4AF37]/20 bg-black/40 backdrop-blur-md flex items-center justify-center text-white/50 hover:text-[#D4AF37] hover:border-[#D4AF37]/60 disabled:opacity-20 transition-all duration-300" data-id="element-7066">
          <ArrowLeft className="w-5 h-5" data-id="element-7067" />
        </button>
        <button onClick={nextSlide} disabled={activeSlide === totalSlides - 1} className="w-12 h-12 rounded-full border border-[#D4AF37]/20 bg-black/40 backdrop-blur-md flex items-center justify-center text-white/50 hover:text-[#D4AF37] hover:border-[#D4AF37]/60 disabled:opacity-20 transition-all duration-300" data-id="element-7068">
          <ArrowRight className="w-5 h-5" data-id="element-7069" />
        </button>
      </div>

      {/* Slides Container */}
      <motion.div className="flex h-full w-full" animate={{
      x: `-${activeSlide * 100}vw`
    }} transition={{
      type: 'spring',
      stiffness: 150,
      damping: 25,
      mass: 0.8
    }} data-id="element-7070">
        {/* 1. Cover */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7071">
          <SlideAtmosphere imageUrl="https://cdn.magicpatterns.com/uploads/7ngvtUG3Hhfrt7dJK3EcMQ/Gemini_Generated_Image_b3829nb3829nb382.png" opacity={0.5} overlayGradient="gold" withParticles data-id="element-7072" />
          <PunjabiPlacard word="ਸ਼ੁਰੂਆਤ" translation="Beginning" data-id="element-7073" />

          <div className="relative z-10 text-center px-6 max-w-4xl mx-auto" data-id="element-7074">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1.2,
            ease: 'easeOut'
          }} data-id="element-7075">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/a1thpmaUHZUisJ3HgEhAjA/oldkhanda.png" alt="Khanda" className="w-16 h-16 mx-auto opacity-70 mb-8 mix-blend-screen" data-id="element-7076" />
              <h1 className="font-serif text-6xl sm:text-7xl md:text-8xl text-[#FDFBF7] mb-6 leading-none drop-shadow-2xl" data-id="element-7077">
                Kingdoms of Punjab
              </h1>
              <p className="text-xl sm:text-2xl text-[#D4AF37] font-light tracking-[0.2em] uppercase mb-8" data-id="element-7078">
                Life in the Sikh Kingdoms
              </p>
              <div className="w-px h-16 bg-gradient-to-b from-[#D4AF37] to-transparent mx-auto" data-id="element-7079" />
            </motion.div>
          </div>
        </section>

        {/* 2. Opening Truth */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7080">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-7081" />
          <PunjabiPlacard word="ਵਿਰਾਸਤ" translation="Heritage" data-id="element-7082" />

          <div className="relative z-10 max-w-4xl mx-auto text-center px-6" data-id="element-7083">
            <motion.figure initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1.5
          }} className="mb-16" data-id="element-7084">
              <blockquote className="font-serif text-xl sm:text-2xl text-[#D4AF37] italic leading-relaxed" data-id="element-7085">
                "A people without knowledge of their past history, origin and
                culture is like a tree without roots."
              </blockquote>
              <figcaption className="text-white/40 text-xs tracking-[0.3em] uppercase mt-6" data-id="element-7086">
                — Winston Churchill
              </figcaption>
            </motion.figure>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1.2,
            delay: 0.3
          }} data-id="element-7087">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] leading-tight mb-8" data-id="element-7088">
                We inherited a civilization.
                <br data-id="element-7089" />
                <span className="text-[#D4AF37]" data-id="element-7090">
                  Our children inherited fragments.
                </span>
              </h2>
              <p className="text-lg text-white/60 font-light max-w-2xl mx-auto" data-id="element-7091">
                When a people forget their history, they risk losing the courage
                and dignity it was meant to give them.
              </p>
            </motion.div>
          </div>
        </section>

        {/* 3. ROM Legacy */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7092">
          <SlideAtmosphere imageUrl="https://cdn.magicpatterns.com/uploads/mdkRi9opWdPpM6cSCo7Zgm/image.png" opacity={0.2} overlayGradient="dark" data-id="element-7093" />
          <PunjabiPlacard word="ਪ੍ਰਮਾਣ" translation="Proof" data-id="element-7094" />

          <div className="relative z-10 max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center gap-16" data-id="element-7095">
            <motion.div className="flex-1" initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 1
          }} data-id="element-7096">
              <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-6" data-id="element-7097">
                The Precedent
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] leading-tight mb-6" data-id="element-7098">
                Our community has proven this story matters.
              </h2>
              <p className="text-lg text-white/60 font-light" data-id="element-7099">
                <span className="text-[#D4AF37]" data-id="element-7100">
                  Arts of the Sikh Kingdoms
                </span>{' '}
                remains the second most successful exhibition in Canadian
                history at the Royal Ontario Museum.
              </p>
            </motion.div>
            <motion.div className="flex-1 w-full max-w-sm" initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1.5
          }} data-id="element-7101">
              <div className="aspect-[3/4] p-3 bg-[#110c08] border border-[#D4AF37]/20 shadow-[0_20px_50px_rgba(0,0,0,0.5)]" data-id="element-7102">
                <img src="https://cdn.magicpatterns.com/uploads/mdkRi9opWdPpM6cSCo7Zgm/image.png" alt="ROM Exhibition" className="w-full h-full object-cover transition-all duration-700 hover:scale-105" data-id="element-7103" />
              </div>
            </motion.div>
          </div>
        </section>

        {/* 4. Epiphany & Trailer */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex flex-col items-center justify-center" data-id="element-7104">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7105" />
          <PunjabiPlacard word="ਜਾਗ੍ਰਿਤੀ" translation="Awakening" data-id="element-7106" />

          <div className="relative z-10 max-w-6xl mx-auto w-full px-6 text-center" data-id="element-7107">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1
          }} data-id="element-7108">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] mb-12" data-id="element-7109">
                What if Sikh history could feel alive?
              </h2>
              <div className="w-full max-w-4xl mx-auto aspect-video bg-black border border-[#D4AF37]/20 shadow-[0_0_50px_rgba(212,175,55,0.1)] relative p-1" data-id="element-7110">
                <iframe src="https://www.youtube.com/embed/2yMWvJinVgk" title="Trailer" allowFullScreen className="w-full h-full" data-id="element-7111" />
              </div>
            </motion.div>
          </div>
        </section>

        {/* 5. Vision */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7112">
          <SlideAtmosphere imageUrl="https://uploadthingy.s3.us-west-1.amazonaws.com/rru8mxg1f864Swgj1TtDBp/Gemini_Generated_Image_xf598lxf598lxf59.png" opacity={0.15} overlayGradient="gold" data-id="element-7113" />
          <PunjabiPlacard word="ਮੰਜ਼ਿਲ" translation="Destination" data-id="element-7114" />

          <div className="relative z-10 max-w-4xl mx-auto text-center px-6" data-id="element-7115">
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1.5
          }} data-id="element-7116">
              <p className="text-[#D4AF37] text-xs tracking-[0.4em] uppercase mb-8" data-id="element-7117">
                The Goal
              </p>
              <h2 className="font-serif text-5xl sm:text-6xl text-[#FDFBF7] leading-tight mb-8" data-id="element-7118">
                The most successful immersive experience in Canadian history.
              </h2>
              <div className="w-px h-12 bg-gradient-to-b from-[#D4AF37]/50 to-transparent mx-auto" data-id="element-7119" />
            </motion.div>
          </div>
        </section>

        {/* 6. Step Inside */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7120">
          <SlideAtmosphere videoUrl="https://cdn.magicpatterns.com/uploads/2vbujNeh6XCJnC6Xt6Yz87/animate_this_image_so_its_like.mp4" opacity={1} overlayGradient="dark" lightScrim videoBrightness={1.6} playbackRate={0.5} data-id="element-7121" />
          <PunjabiPlacard word="ਦਰਬਾਰ" translation="Court" data-id="element-7122" />

          <div className="absolute top-24 left-0 right-0 z-10 max-w-4xl mx-auto text-center px-6" data-id="element-7123">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1.2
          }} data-id="element-7124">
              <p className="font-serif text-2xl sm:text-3xl text-[#FDFBF7] font-light drop-shadow-[0_10px_30px_rgba(0,0,0,0.8)]" data-id="element-7125">
                Step into a world of empire, diplomacy, art, and courage.
              </p>
            </motion.div>
          </div>
        </section>

        {/* 7. Portraits That Awaken */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7126">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-7127" />
          <PunjabiPlacard word="ਦਰਸ਼ਨ" translation="Vision" data-id="element-7128" />

          <div className="relative z-10 max-w-5xl mx-auto w-full px-6 flex flex-col md:flex-row items-center gap-12" data-id="element-7129">
            <motion.div className="flex-1 text-center md:text-left" initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 1
          }} data-id="element-7130">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] mb-6" data-id="element-7131">
                Historical figures come to life.
              </h2>
              <p className="text-lg text-white/60 font-light" data-id="element-7132">
                Maharaja Ranjit Singh and others awaken on screen to guide
                students through the Darbar.
              </p>
            </motion.div>
            <motion.div className="flex-1 w-full flex justify-center" initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1.5
          }} data-id="element-7133">
              <div className="h-[60vh] max-h-[500px] aspect-[9/16] relative p-4 bg-[#1a140f] border-[16px] border-[#8a6b22] shadow-[0_30px_60px_rgba(0,0,0,0.9),inset_0_0_30px_rgba(0,0,0,0.9)] rounded-sm before:absolute before:inset-0 before:border-[2px] before:border-[#D4AF37]/30 before:m-1.5" data-id="element-7134">
                <div className="w-full h-full relative overflow-hidden shadow-[inset_0_0_50px_rgba(0,0,0,0.9)] bg-black" data-id="element-7135">
                  <video src="https://cdn.magicpatterns.com/uploads/oAYvh93WVHg7sLuQmvLMws/WhatsApp%20Video%202026-06-08%20at%2009.07.35.mp4" autoPlay loop muted playsInline className="w-full h-full object-cover" data-id="element-7136" />
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 8. History Speaks Back */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7137">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7138" />
          <PunjabiPlacard word="ਸੰਵਾਦ" translation="Dialogue" data-id="element-7139" />

          <div className="relative z-10 max-w-5xl mx-auto w-full px-6 flex flex-col md:flex-row-reverse items-center gap-12" data-id="element-7140">
            <motion.div className="flex-1 text-center md:text-left" initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 1
          }} data-id="element-7141">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] mb-6" data-id="element-7142">
                From watching history to speaking with it.
              </h2>
              <p className="text-lg text-white/60 font-light" data-id="element-7143">
                Using live avatar technology, students ask questions and receive
                historically informed responses in real time.
              </p>
            </motion.div>
            <motion.div className="flex-1 w-full flex justify-center" initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1.5
          }} data-id="element-7144">
              <div className="h-[60vh] max-h-[500px] aspect-[9/16] relative p-4 bg-[#1a140f] border-[16px] border-[#8a6b22] shadow-[0_30px_60px_rgba(0,0,0,0.9),inset_0_0_30px_rgba(0,0,0,0.9)] rounded-sm before:absolute before:inset-0 before:border-[2px] before:border-[#D4AF37]/30 before:m-1.5" data-id="element-7145">
                <div className="w-full h-full relative overflow-hidden shadow-[inset_0_0_50px_rgba(0,0,0,0.9)] bg-black" data-id="element-7146">
                  <video src="https://cdn.magicpatterns.com/uploads/tHe5vNNjxpJUtnsEoB8t32/WhatsApp%20Video%202026-05-26%20at%2015.06.40.mp4" autoPlay loop muted playsInline className="w-full h-full object-cover" data-id="element-7147" />
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 9. Living Archive */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7148">
          <SlideAtmosphere overlayGradient="gold" withTexture data-id="element-7149" />
          <PunjabiPlacard word="ਯਾਦ" translation="Memory" data-id="element-7150" />

          <div className="relative z-10 max-w-6xl mx-auto px-6 w-full" data-id="element-7151">
            <motion.div className="text-center mb-12" initial={{
            opacity: 0,
            y: -20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1
          }} data-id="element-7152">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] mb-4" data-id="element-7153">
                A living archive of memories.
              </h2>
              <p className="text-lg text-white/60 font-light" data-id="element-7154">
                Cinematic scenes of battles, diplomacy, and sacred spaces.
              </p>
            </motion.div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-7155">
              {['https://uploadthingy.s3.us-west-1.amazonaws.com/rru8mxg1f864Swgj1TtDBp/Gemini_Generated_Image_xf598lxf598lxf59.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/2uRFx3az1Ndo6QPmwLotNT/Gemini_Generated_Image_6xdim76xdim76xdi.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/wVBxvpncFWjZ3U7V1C4TbV/Gemini_Generated_Image_cc8os5cc8os5cc8o.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/iav6C11v1iAqRNyzsLVGur/Gemini_Generated_Image_ouvfm7ouvfm7ouvf.png'].map((src, i) => <motion.div key={i} initial={{
              opacity: 0,
              scale: 0.9
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 1,
              delay: i * 0.2
            }} className="aspect-[4/5] bg-[#0a0604] border border-[#D4AF37]/20 relative overflow-hidden group" data-id="element-7156">
                  <img src={src} alt="Archive" className="w-full h-full object-cover group-hover:scale-105 transition-all duration-1000" data-id="element-7157" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" data-id="element-7158" />
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* 10. Scent, Sound, Taste */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7159">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7160" />
          <PunjabiPlacard word="ਅਹਿਸਾਸ" translation="Feeling" data-id="element-7161" />

          <div className="relative z-10 max-w-6xl mx-auto px-6 w-full" data-id="element-7162">
            <motion.div className="text-center mb-16" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1
          }} data-id="element-7163">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] mb-4" data-id="element-7164">
                Not just seen. Felt.
              </h2>
              <p className="text-lg text-white/60 font-light" data-id="element-7165">
                Sound, scent, and taste make history unforgettable.
              </p>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-7166">
              {[{
              src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/e7tNmw42aGJMhG2K8pg9sq/Sher-e-Punjab_Lassi_%28Lion_of_Punjab_Smoothie%29.webp',
              name: 'Sher-e-Punjab Lassi',
              inspiredBy: 'Inspired by Maharaja Ranjit Singh'
            }, {
              src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/7s7gbBG7Kx151cwzpHK1du/Hari_Singh_Nalwa%E2%80%99s_Tiger_Fury_Sharbat.webp',
              name: 'Tiger Fury Sharbat',
              inspiredBy: 'Inspired by Hari Singh Nalwa'
            }, {
              src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/eKo28eax4tv8HfFsN3KZGY/Chabeel_of_Compassion_%28Rose__Kewra_Cooler%29.webp',
              name: 'Chabeel of Compassion',
              inspiredBy: 'Inspired by Moran Sarkar'
            }, {
              src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/uNnPb6Ex8V8D1T89ouiXSm/Shahi_Aam_Panna_%28Royal_Raw_Mango_Punch%29.webp',
              name: 'Shahi Aam Panna',
              inspiredBy: 'Inspired by Maharani Jind Kaur'
            }].map((drink, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 1,
              delay: i * 0.15
            }} className="text-center group" data-id="element-7167">
                  <div className="aspect-[3/4] mb-5 overflow-hidden border border-[#D4AF37]/20 bg-[#0a0604] relative" data-id="element-7168">
                    <img src={drink.src} alt={drink.name} className="w-full h-full object-cover opacity-60 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" data-id="element-7169" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" data-id="element-7170" />
                  </div>
                  <p className="text-[#D4AF37] text-xs tracking-widest uppercase mb-2" data-id="element-7171">
                    {drink.name}
                  </p>
                  <p className="text-white/40 text-[10px] italic font-light" data-id="element-7172">
                    {drink.inspiredBy}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* 11. In Progress Now */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7173">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-7174" />
          <PunjabiPlacard word="ਸਿਰਜਣਾ" translation="Creation" data-id="element-7175" />

          <div className="relative z-10 max-w-5xl mx-auto px-6 w-full" data-id="element-7176">
            <motion.div className="text-center mb-12" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1
          }} data-id="element-7177">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] mb-4" data-id="element-7178">
                This is no longer just an idea.
              </h2>
              <p className="text-lg text-white/60 font-light" data-id="element-7179">
                Testing the environment, scale, and production pathway.
              </p>
            </motion.div>

            <div className="grid sm:grid-cols-2 gap-6" data-id="element-7180">
              {['https://cdn.magicpatterns.com/uploads/a7heywR1FeDYnub9ua9WT9/WhatsApp_Image_2026-06-12_at_14.36.03_(1).jpg', 'https://cdn.magicpatterns.com/uploads/ssJFUVqUnzkDMnb4yqvNvT/WhatsApp_Image_2026-06-13_at_10.33.54.jpg', 'https://cdn.magicpatterns.com/uploads/kQxbd1wr2FNT6U2x9evwFs/WhatsApp_Image_2026-06-12_at_14.36.03_(6).jpg', 'https://cdn.magicpatterns.com/uploads/pnghcT8rVnDqxy4q3pgpBM/WhatsApp_Image_2026-06-12_at_14.36.03_(7).jpg'].map((src, i) => <motion.div key={i} onClick={() => setSelectedImage(src)} initial={{
              opacity: 0,
              scale: 0.95
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.8,
              delay: i * 0.1
            }} className="aspect-video bg-[#0a0604] border border-[#D4AF37]/20 relative overflow-hidden cursor-pointer group" data-id="element-7181">
                  <img src={src} alt="Progress" className="w-full h-full object-cover group-hover:scale-105 transition-all duration-700" data-id="element-7182" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center" data-id="element-7183">
                    <span className="opacity-0 group-hover:opacity-100 text-white/90 text-xs tracking-widest uppercase font-light transition-opacity duration-500" data-id="element-7184">
                      Enlarge
                    </span>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* 12. Pathway */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7185">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7186" />
          <PunjabiPlacard word="ਸਿੱਖਿਆ" translation="Education" data-id="element-7187" />

          <div className="relative z-10 max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center gap-16" data-id="element-7188">
            <motion.div className="flex-1" initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 1
          }} data-id="element-7189">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] leading-tight mb-6" data-id="element-7190">
                From cultural experience to curriculum pathway.
              </h2>
              <p className="text-lg text-white/60 font-light mb-8" data-id="element-7191">
                Piloting at{' '}
                <span className="text-[#D4AF37]" data-id="element-7192">
                  Bramalea Secondary School
                </span>
                , expanding to 15 schools across Canada to bring Sikh history
                into the education system.
              </p>
            </motion.div>
            <motion.div className="flex-1 w-full" initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 1,
            delay: 0.2
          }} data-id="element-7193">
              <div className="border-l-[2px] border-[#D4AF37]/50 pl-8 py-4 relative" data-id="element-7194">
                <div className="absolute -left-[1px] top-0 w-[2px] h-12 bg-[#D4AF37]" data-id="element-7195" />
                <p className="font-serif text-2xl sm:text-3xl text-[#D4AF37] mb-6 leading-relaxed italic" data-id="element-7196">
                  "Our community does not get enough space in historical
                  education. This is a powerful opportunity."
                </p>
                <p className="text-white/40 text-xs tracking-widest uppercase font-light" data-id="element-7197">
                  — Principal, Bramalea Secondary School
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 13. Treasury Cards */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7198">
          <SlideAtmosphere overlayGradient="gold" withTexture data-id="element-7199" />
          <PunjabiPlacard word="ਖ਼ਜ਼ਾਨਾ" translation="Treasury" data-id="element-7200" />

          <div className="relative z-10 max-w-6xl mx-auto px-6 flex flex-col lg:flex-row items-center gap-12" data-id="element-7201">
            <motion.div className="flex-1 text-center lg:text-left" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1
          }} data-id="element-7202">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] leading-tight mb-6" data-id="element-7203">
                Pokémon meets Magic: The Gathering meets Sikh history.
              </h2>
              <p className="text-lg text-white/60 font-light mb-8" data-id="element-7204">
                Students learn by collecting, unlocking, and competing.
              </p>
              <div className="flex flex-wrap justify-center lg:justify-start gap-4" data-id="element-7205">
                {['Quests', 'Rare Cards', 'Leaderboards'].map((item, i) => <div key={i} className="border border-[#D4AF37]/30 bg-black/40 px-4 py-2 flex items-center gap-2 backdrop-blur-sm" data-id="element-7206">
                    <Star className="w-3 h-3 text-[#D4AF37]" data-id="element-7207" />
                    <span className="text-white/70 text-xs tracking-widest uppercase" data-id="element-7208">
                      {item}
                    </span>
                  </div>)}
              </div>
            </motion.div>

            <div className="flex-1 w-full" data-id="element-7209">
              <div className="grid grid-cols-2 gap-4" data-id="element-7210">
                {[{
                src: 'https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png',
                name: 'Ranjit Singh',
                desc: 'The Lion of Punjab, founder of the Sikh Empire.',
                stats: [{
                  label: 'Rarity',
                  value: 'Legendary'
                }, {
                  label: 'Type',
                  value: 'Ruler'
                }]
              }, {
                src: 'https://cdn.magicpatterns.com/uploads/nVFG4hPLbQSgSkuEzm99Kr/Jind_Kaur.png',
                name: 'Jind Kaur',
                desc: 'The fearless Maharani who stood against the British.',
                stats: [{
                  label: 'Rarity',
                  value: 'Epic'
                }, {
                  label: 'Type',
                  value: 'Queen'
                }]
              }, {
                src: 'https://cdn.magicpatterns.com/uploads/ekyaLxoHxw9b6NSM5wgnik/Hari_Singh_Nalwa.png',
                name: 'Hari Singh Nalwa',
                desc: 'The legendary commander of the Sikh Khalsa Army.',
                stats: [{
                  label: 'Rarity',
                  value: 'Legendary'
                }, {
                  label: 'Type',
                  value: 'Warrior'
                }]
              }, {
                src: 'https://cdn.magicpatterns.com/uploads/st6W3BtxYpFsMW4u23CAxv/Harmandir_Sahib_Amritsar.png',
                name: 'Harmandir Sahib',
                desc: 'The golden spiritual center of the Sikh faith.',
                stats: [{
                  label: 'Rarity',
                  value: 'Mythic'
                }, {
                  label: 'Type',
                  value: 'Sacred Site'
                }]
              }].map((card, i) => <motion.div key={i} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.8,
                delay: i * 0.15
              }} data-id="element-7211">
                    <FlippableCard card={card} data-id="element-7212" />
                  </motion.div>)}
              </div>
            </div>
          </div>
        </section>

        {/* 14. School to Show */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7213">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-7214" />
          <PunjabiPlacard word="ਸਫ਼ਰ" translation="Journey" data-id="element-7215" />

          <div className="relative z-10 max-w-4xl mx-auto text-center px-6" data-id="element-7216">
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1.5
          }} data-id="element-7217">
              <h2 className="font-serif text-5xl sm:text-6xl text-[#FDFBF7] leading-tight mb-12" data-id="element-7218">
                First they unlock the story.
                <br data-id="element-7219" />
                <span className="text-[#D4AF37]" data-id="element-7220">
                  Then they step inside it.
                </span>
              </h2>
              <div className="flex flex-wrap items-center justify-center gap-4 text-xs tracking-[0.2em] uppercase font-light text-white/50" data-id="element-7221">
                <span data-id="element-7222">School</span>
                <span className="text-[#D4AF37] opacity-50" data-id="element-7223">→</span>
                <span data-id="element-7224">Cards</span>
                <span className="text-[#D4AF37] opacity-50" data-id="element-7225">→</span>
                <span data-id="element-7226">Bus</span>
                <span className="text-[#D4AF37] opacity-50" data-id="element-7227">→</span>
                <span className="text-[#D4AF37]" data-id="element-7228">Immersive Show</span>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Sneak Peek (moved after School to Show) */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7229">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7230" />
          <PunjabiPlacard word="ਝਲਕ" translation="Glimpse" data-id="element-7231" />

          <div className="relative z-10 max-w-5xl mx-auto w-full px-6 text-center" data-id="element-7232">
            <motion.div initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1
          }} data-id="element-7233">
              <h2 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] mb-8" data-id="element-7234">
                A glimpse of the experience.
              </h2>
              <div className="aspect-video bg-black border border-[#D4AF37]/20 shadow-[0_0_50px_rgba(212,175,55,0.1)] relative p-1" data-id="element-7235">
                <iframe src="https://www.youtube.com/embed/pW6rFd_Td0o?controls=0&rel=0&modestbranding=1&showinfo=0" title="Sneak Peek" allowFullScreen className="w-full h-full" data-id="element-7236" />
              </div>
            </motion.div>
          </div>
        </section>

        {/* 15. Access Problem */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7237">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7238" />
          <PunjabiPlacard word="ਪਹੁੰਚ" translation="Access" data-id="element-7239" />

          <div className="relative z-10 max-w-4xl mx-auto text-center px-6" data-id="element-7240">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1.2
          }} data-id="element-7241">
              <h2 className="font-serif text-5xl sm:text-6xl text-[#FDFBF7] leading-tight mb-8" data-id="element-7242">
                The barrier should not be a bus.
              </h2>
              <p className="text-lg text-white/60 font-light max-w-2xl mx-auto" data-id="element-7243">
                If we want this to reach the next generation, we must remove the
                financial barriers to transportation and access.
              </p>
            </motion.div>
          </div>
        </section>

        {/* 16. First Year Goal */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7244">
          <SlideAtmosphere overlayGradient="gold" withTexture data-id="element-7245" />
          <PunjabiPlacard word="ਨਿਸ਼ਾਨਾ" translation="Target" data-id="element-7246" />

          <div className="relative z-10 max-w-4xl mx-auto text-center px-6" data-id="element-7247">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1.5
          }} data-id="element-7248">
              <h2 className="font-serif text-7xl sm:text-8xl text-[#FDFBF7] leading-none mb-4" data-id="element-7249">
                12,000
              </h2>
              <p className="text-xl tracking-[0.3em] uppercase text-[#D4AF37] font-light mb-8" data-id="element-7250">
                Students in Year One
              </p>
              <div className="w-px h-12 bg-gradient-to-b from-[#D4AF37]/50 to-transparent mx-auto" data-id="element-7251" />
            </motion.div>
          </div>
        </section>

        {/* 18. National Moments */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7252">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-7253" />
          <PunjabiPlacard word="ਲਹਿਰ" translation="Movement" data-id="element-7254" />

          <div className="relative z-10 max-w-4xl mx-auto px-6 text-center" data-id="element-7255">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1.2
          }} data-id="element-7256">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] leading-tight mb-12" data-id="element-7257">
                This becomes a Canadian cultural moment.
              </h2>
              <div className="flex flex-col sm:flex-row gap-8 justify-center text-left" data-id="element-7258">
                <div className="flex-1 border-t border-[#D4AF37]/30 pt-6" data-id="element-7259">
                  <p className="text-[#D4AF37] text-xs tracking-widest uppercase mb-3" data-id="element-7260">
                    Nov 2026
                  </p>
                  <p className="text-white/70 font-light text-sm" data-id="element-7261">
                    Premier of Ontario & Provincial Government
                  </p>
                </div>
                <div className="flex-1 border-t border-[#D4AF37]/30 pt-6" data-id="element-7262">
                  <p className="text-[#D4AF37] text-xs tracking-widest uppercase mb-3" data-id="element-7263">
                    Apr 2027
                  </p>
                  <p className="text-white/70 font-light text-sm" data-id="element-7264">
                    Prime Minister & Federal Government in Ottawa
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 19. Ask Context */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7265">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7266" />
          <PunjabiPlacard word="ਸਰਪ੍ਰਸਤ" translation="Patron" data-id="element-7267" />

          <div className="relative z-10 max-w-4xl mx-auto text-center px-6" data-id="element-7268">
            <motion.div initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1.5
          }} data-id="element-7269">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] leading-tight mb-8" data-id="element-7270">
                Become a Founding Patron of the Sikh Kingdoms.
              </h2>
              <p className="text-xl text-white/70 font-light mb-12" data-id="element-7271">
                We have committed{' '}
                <span className="text-[#D4AF37]" data-id="element-7272">$500,000</span> to begin. We
                ask the community to help carry it forward.
              </p>
              <div className="grid sm:grid-cols-2 gap-4 text-left max-w-2xl mx-auto" data-id="element-7273">
                {['Sponsor your school', 'Fund student access', 'Production upgrades', 'National expansion'].map((item, i) => <div key={i} className="flex items-center gap-3 border border-[#D4AF37]/20 bg-black/40 p-4 backdrop-blur-sm" data-id="element-7274">
                    <CheckCircle2 className="w-4 h-4 text-[#D4AF37]" data-id="element-7275" />
                    <span className="text-white/70 text-sm font-light" data-id="element-7276">
                      {item}
                    </span>
                  </div>)}
              </div>
            </motion.div>
          </div>
        </section>

        {/* 20. Ask Tiers */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7277">
          <SlideAtmosphere overlayGradient="gold" withTexture data-id="element-7278" />
          <PunjabiPlacard word="ਵਿਰਸਾ" translation="Legacy" data-id="element-7279" />

          <div className="relative z-10 max-w-6xl mx-auto px-6 w-full" data-id="element-7280">
            <div className="text-center mb-10" data-id="element-7281">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] mb-4" data-id="element-7282">
                Choose your legacy.
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-10" data-id="element-7283">
              {[{
              price: '$10,000',
              title: 'Patron of the Bus',
              icon: Building2,
              items: ['Sponsor student transportation', 'Recognition on sponsor night', 'Invite a school']
            }, {
              price: '$25,000',
              title: 'Founding School Patron',
              icon: Users,
              items: ['Sponsor a school activation', 'Family banner on your night', 'Recognition inside experience']
            }, {
              price: '$50,000',
              title: 'Legacy Patron',
              icon: Crown,
              items: ['Support access & expansion', 'Premium family recognition', 'Maharaja acknowledgement moment']
            }].map((tier, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              delay: i * 0.1
            }} className="border border-[#D4AF37]/30 bg-[#0a0604]/80 p-6 backdrop-blur-md relative overflow-hidden" data-id="element-7284">
                  <div className="absolute top-0 right-0 p-4 opacity-10" data-id="element-7285">
                    <tier.icon className="w-20 h-20 text-[#D4AF37]" data-id="element-7286" />
                  </div>
                  <h3 className="font-serif text-3xl text-[#FDFBF7] mb-2" data-id="element-7287">
                    {tier.price}
                  </h3>
                  <p className="text-[#D4AF37] text-xs tracking-widest uppercase mb-6" data-id="element-7288">
                    {tier.title}
                  </p>
                  <ul className="space-y-3" data-id="element-7289">
                    {tier.items.map((item, j) => <li key={j} className="text-white/60 text-sm font-light flex gap-2" data-id="element-7290">
                        <span className="text-[#D4AF37] opacity-50" data-id="element-7291">•</span>{' '}
                        {item}
                      </li>)}
                  </ul>
                </motion.div>)}
            </div>

            <div className="text-center flex flex-col items-center" data-id="element-7292">
              <div className="bg-white p-2 rounded-sm shadow-[0_0_30px_rgba(212,175,55,0.3)] mb-4" data-id="element-7293">
                <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=https://typeform.com" alt="Scan to commit" className="w-20 h-20" data-id="element-7294" />
              </div>
              <p className="text-[#D4AF37] text-[10px] tracking-[0.3em] uppercase mb-2" data-id="element-7295">
                Scan to commit
              </p>
              <p className="text-white/40 text-[10px] italic font-light" data-id="element-7296">
                Contributions may be eligible for tax receipts where applicable.
              </p>
            </div>
          </div>
        </section>

        {/* 21. FAQ */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7297">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7298" />
          <PunjabiPlacard word="ਗਿਆਨ" translation="Knowledge" data-id="element-7299" />

          <div className="relative z-10 max-w-5xl mx-auto px-6 w-full" data-id="element-7300">
            <div className="text-center mb-10" data-id="element-7301">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] mb-4" data-id="element-7302">
                Giving &amp; tax, simply.
              </h2>
              <p className="text-lg text-white/60 font-light" data-id="element-7303">
                A plain-language guide to how contributions are receipted.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6" data-id="element-7304">
              <div className="border border-[#D4AF37]/20 bg-[#0a0604]/60 p-6 backdrop-blur-sm" data-id="element-7305">
                <div className="flex items-center gap-3 mb-4" data-id="element-7306">
                  <Receipt className="w-5 h-5 text-[#D4AF37]" data-id="element-7307" />
                  <h3 className="font-serif text-xl text-[#FDFBF7]" data-id="element-7308">
                    What might a $10,000 gift cost me?
                  </h3>
                </div>
                <p className="text-sm text-white/70 font-light leading-relaxed mb-4" data-id="element-7309">
                  An Ontario donor contributing{' '}
                  <span className="text-[#FDFBF7]" data-id="element-7310">$10,000</span> to a
                  registered charity may receive roughly{' '}
                  <span className="text-[#D4AF37]" data-id="element-7311">$3,975–$4,000</span> in
                  combined federal and provincial charitable tax credits —
                  making the <span className="text-[#FDFBF7]" data-id="element-7312">estimated</span>{' '}
                  after-tax cost about{' '}
                  <span className="text-[#D4AF37]" data-id="element-7313">$6,000</span>.
                </p>
                <p className="text-[10px] text-white/40 italic leading-relaxed" data-id="element-7314">
                  An estimate, not a guaranteed refund. Charitable credits are
                  non-refundable: they reduce taxes you owe, and depend on your
                  personal tax situation and the eligible receiptable amount.
                </p>
              </div>

              <div className="border border-[#D4AF37]/20 bg-[#0a0604]/60 p-6 backdrop-blur-sm" data-id="element-7315">
                <div className="flex items-center gap-3 mb-4" data-id="element-7316">
                  <Info className="w-5 h-5 text-[#D4AF37]" data-id="element-7317" />
                  <h3 className="font-serif text-xl text-[#FDFBF7]" data-id="element-7318">
                    How the credit works
                  </h3>
                </div>
                <ul className="space-y-3 text-sm text-white/70 font-light" data-id="element-7319">
                  <li data-id="element-7320">
                    <span className="text-[#D4AF37] mr-2" data-id="element-7321">•</span>First $200
                    credited at ~20%; remainder at ~40%.
                  </li>
                  <li data-id="element-7322">
                    <span className="text-[#D4AF37] mr-2" data-id="element-7323">•</span>Credits apply
                    only up to tax otherwise payable.
                  </li>
                  <li data-id="element-7324">
                    <span className="text-[#D4AF37] mr-2" data-id="element-7325">•</span>Claimable up
                    to 75% of net income.
                  </li>
                  <li data-id="element-7326">
                    <span className="text-[#D4AF37] mr-2" data-id="element-7327">•</span>Unused amounts
                    carry forward up to 5 years.
                  </li>
                </ul>
              </div>
            </div>

            <div className="border border-[#D4AF37]/20 bg-[#0a0604]/60 p-6 backdrop-blur-sm" data-id="element-7328">
              <div className="flex items-center gap-3 mb-4" data-id="element-7329">
                <FileText className="w-5 h-5 text-[#D4AF37]" data-id="element-7330" />
                <h3 className="font-serif text-xl text-[#FDFBF7]" data-id="element-7331">
                  Receipting Structure
                </h3>
              </div>
              <div className="grid sm:grid-cols-3 gap-6" data-id="element-7332">
                <div data-id="element-7333">
                  <p className="text-[#D4AF37] text-xs tracking-widest uppercase mb-2" data-id="element-7334">
                    Donation
                  </p>
                  <p className="text-xs text-white/60 font-light" data-id="element-7335">
                    Charitable gift with no material benefit. Full receipt.
                  </p>
                </div>
                <div data-id="element-7336">
                  <p className="text-[#D4AF37] text-xs tracking-widest uppercase mb-2" data-id="element-7337">
                    Sponsorship
                  </p>
                  <p className="text-xs text-white/60 font-light" data-id="element-7338">
                    Marketing value. Business expense, not a charitable receipt.
                  </p>
                </div>
                <div data-id="element-7339">
                  <p className="text-[#D4AF37] text-xs tracking-widest uppercase mb-2" data-id="element-7340">
                    Hybrid
                  </p>
                  <p className="text-xs text-white/60 font-light" data-id="element-7341">
                    Partial receipt only for amount above fair-market value of
                    benefits.
                  </p>
                </div>
              </div>
              <p className="text-[10px] text-white/40 italic mt-6 flex items-start gap-2" data-id="element-7342">
                <HandCoins className="w-3 h-3 text-[#D4AF37] shrink-0 mt-0.5" data-id="element-7343" />
                If you receive tickets, dinners, or VIP access, CRA treats their
                value as an advantage — the receipt covers only the eligible
                gift after subtracting that value. This is general information,
                not tax advice; please confirm with your advisor.
              </p>
            </div>
          </div>
        </section>

        {/* 22. Why Ash Singh */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7344">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-7345" />
          <PunjabiPlacard word="ਭਰੋਸਾ" translation="Trust" data-id="element-7346" />

          <div className="relative z-10 max-w-6xl mx-auto px-6 w-full" data-id="element-7347">
            <div className="text-center mb-12" data-id="element-7348">
              <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4" data-id="element-7349">
                Why Ash Singh
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] max-w-4xl mx-auto" data-id="element-7350">
                Because this project needs culture, education, technology, and
                trust.
              </h2>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-7351">
              <div data-id="element-7352">
                <p className="text-lg text-white/80 font-light mb-6" data-id="element-7353">
                  <span className="text-[#FDFBF7]" data-id="element-7354">25+ years</span> building at
                  the intersection of Sikh identity, education, technology, and
                  public experience.
                </p>
                <ul className="space-y-3 text-sm text-white/60 font-light" data-id="element-7355">
                  <li data-id="element-7356">
                    <span className="text-[#D4AF37] mr-2" data-id="element-7357">•</span>Founder of
                    SikhiWiki (65M+ visitors) & Turbanizer
                  </li>
                  <li data-id="element-7358">
                    <span className="text-[#D4AF37] mr-2" data-id="element-7359">•</span>Global
                    entrepreneurship educator (INSEAD, Ivey, Wharton)
                  </li>
                  <li data-id="element-7360">
                    <span className="text-[#D4AF37] mr-2" data-id="element-7361">•</span>Son of the
                    leaders behind Arts of the Sikh Kingdoms
                  </li>
                  <li data-id="element-7362">
                    <span className="text-[#D4AF37] mr-2" data-id="element-7363">•</span>Lead sevadar
                    supporting the Sikh Art & Culture Gallery at ROM
                  </li>
                  <li data-id="element-7364">
                    <span className="text-[#D4AF37] mr-2" data-id="element-7365">•</span>Producer of
                    large-scale community experiences & TV
                  </li>
                  <li data-id="element-7366">
                    <span className="text-[#D4AF37] mr-2" data-id="element-7367">•</span>Former owner
                    of Singapore's pro basketball team
                  </li>
                  <li data-id="element-7368">
                    <span className="text-[#D4AF37] mr-2" data-id="element-7369">•</span>Startup
                    professor, coder, and AI builder
                  </li>
                </ul>
              </div>

              <div className="flex justify-center" data-id="element-7370">
                <div className="relative w-[300px] h-[300px]" data-id="element-7371">
                  <div className="absolute inset-0 rounded-full border border-[#D4AF37]/20 animate-[spin_60s_linear_infinite]" data-id="element-7372" />
                  <div className="absolute inset-8 rounded-full border border-[#D4AF37]/10 animate-[spin_40s_linear_infinite_reverse]" data-id="element-7373" />

                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10" data-id="element-7374">
                    <div className="w-24 h-24 rounded-full bg-[#110c08] border border-[#D4AF37]/50 flex flex-col items-center justify-center shadow-[0_0_30px_rgba(212,175,55,0.2)]" data-id="element-7375">
                      <Crown className="w-6 h-6 text-[#D4AF37] mb-1" data-id="element-7376" />
                      <p className="font-serif text-[#FDFBF7] text-sm" data-id="element-7377">
                        Ash Singh
                      </p>
                    </div>
                  </div>

                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 text-center" data-id="element-7378">
                    <div className="bg-[#0a0604]/80 border border-[#D4AF37]/30 rounded p-2 backdrop-blur-sm" data-id="element-7379">
                      <Globe className="w-4 h-4 text-[#D4AF37] mx-auto mb-1" data-id="element-7380" />
                      <p className="text-[8px] text-[#D4AF37] uppercase tracking-widest" data-id="element-7381">
                        Digital Seva
                      </p>
                    </div>
                  </div>
                  <div className="absolute top-1/2 right-0 translate-x-1/2 -translate-y-1/2 w-24 text-center" data-id="element-7382">
                    <div className="bg-[#0a0604]/80 border border-[#D4AF37]/30 rounded p-2 backdrop-blur-sm" data-id="element-7383">
                      <GraduationCap className="w-4 h-4 text-[#D4AF37] mx-auto mb-1" data-id="element-7384" />
                      <p className="text-[8px] text-[#D4AF37] uppercase tracking-widest" data-id="element-7385">
                        Education
                      </p>
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-24 text-center" data-id="element-7386">
                    <div className="bg-[#0a0604]/80 border border-[#D4AF37]/30 rounded p-2 backdrop-blur-sm" data-id="element-7387">
                      <Landmark className="w-4 h-4 text-[#D4AF37] mx-auto mb-1" data-id="element-7388" />
                      <p className="text-[8px] text-[#D4AF37] uppercase tracking-widest" data-id="element-7389">
                        Culture
                      </p>
                    </div>
                  </div>
                  <div className="absolute top-1/2 left-0 -translate-x-1/2 -translate-y-1/2 w-24 text-center" data-id="element-7390">
                    <div className="bg-[#0a0604]/80 border border-[#D4AF37]/30 rounded p-2 backdrop-blur-sm" data-id="element-7391">
                      <Sparkles className="w-4 h-4 text-[#D4AF37] mx-auto mb-1" data-id="element-7392" />
                      <p className="text-[8px] text-[#D4AF37] uppercase tracking-widest" data-id="element-7393">
                        Experience
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center mt-16" data-id="element-7394">
              <p className="font-serif text-xl sm:text-2xl text-[#FDFBF7]" data-id="element-7395">
                This is not a side project.{' '}
                <span className="text-[#D4AF37]" data-id="element-7396">
                  This is the work I have been preparing for my whole life.
                </span>
              </p>
            </div>
          </div>
        </section>
      </motion.div>

      {/* Lightbox Modal */}
      <AnimatePresence data-id="element-7397">
        {selectedImage && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm p-4 sm:p-8 cursor-pointer" onClick={() => setSelectedImage(null)} data-id="element-7398">
            <motion.img initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.95,
          opacity: 0
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} src={selectedImage} alt="Enlarged view" className="max-w-full max-h-full object-contain rounded-sm shadow-[0_0_50px_rgba(212,175,55,0.15)]" data-id="element-7399" />
            <div className="absolute top-6 right-6 text-white/50 text-sm font-light tracking-widest uppercase" data-id="element-7400">
              Close
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
};